import React, { useEffect, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Switch, TextField, Typography, alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import {
  useCreatePlaylistGroupMutation,
  useGetCompanyQuery,
} from "../../Api/attoDeskApi";
import { IPlaylistGroup } from "../../Api/Interface/api.interface";
import { useNotifier } from "../../Core/Notifier";
import { extractBackendError } from "../../Core/extractBackendError";
import { useAppSelector } from "../../Store/hooks";
import { selectIsSuperAdmin, selectCurrentUser } from "../../Store/Auth/AuthSelector";

/**
 * Lightweight "quick create" dialog for the playlist-group picker used on the
 * Device View / Create / Edit pages (see PlaylistGroupSelect).
 *
 * Like CompanyQuickCreateDialog, this hits the SAME endpoint as the full
 * /playlist-groups/create page (POST /api/playlist-groups via
 * useCreatePlaylistGroupMutation) and therefore shares the exact same
 * validation, duplicate-name rule, and permission gate on the backend. The
 * full page is left untouched — operators can still open it to add playlists.
 *
 * Callback contract: on success, `onCreated` fires with the created group and
 * the dialog closes itself. The RTK "playlistGroup" tag invalidation refreshes
 * every group dropdown automatically; the parent uses the returned group to
 * pre-select it (without assigning it to the device).
 */

const PLAYBACK_MODES = [
  { v: "SEQUENTIAL", label: "Sequential", help: "Play each playlist in order, then start over" },
  { v: "LOOP_ALL", label: "Loop All", help: "Continuously loop through every playlist" },
  { v: "RANDOM", label: "Random", help: "Shuffle the order on every cycle" },
];

const labelSx = { fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75 };
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 1.5 },
  },
};

interface PlaylistGroupQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (group: IPlaylistGroup) => void;
  /**
   * Company to pre-select when the dialog opens. Passed by the Device
   * View / Create / Edit pages so a group created from a device page
   * automatically inherits the device's company.
   *
   *   - number  → seed the Company selector with this id, do not force
   *     the selector to be read-only. Super_admin can still override
   *     (they have permission to create groups for any tenant); tenant
   *     users don't see the selector at all, so no override risk.
   *   - null / undefined → keep the current empty-picker behaviour.
   */
  initialCompanyId?: number | null;
}

const PlaylistGroupQuickCreateDialog: React.FC<PlaylistGroupQuickCreateDialogProps> = ({
  open, onClose, onCreated, initialCompanyId,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [createGroup, { isLoading }] = useCreatePlaylistGroupMutation();

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const currentUser = useAppSelector(selectCurrentUser);
  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin || !open }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [playbackMode, setPlaybackMode] = useState("SEQUENTIAL");
  const [isActive, setIsActive] = useState(true);
  const [companyId, setCompanyId] = useState<number | "">("");
  const [touched, setTouched] = useState(false);
  const submitInFlightRef = useRef(false);

  // Reset the form each time the dialog opens so a previous attempt
  // doesn't linger. If the caller passed a device-owned company_id, we
  // seed the picker with it (requirement: opening from a device page
  // must pre-select that device's company, matched by id — never name —
  // and must never fall back to the first company in the list).
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setPlaybackMode("SEQUENTIAL");
      setIsActive(true);
      setCompanyId(
        typeof initialCompanyId === "number" && initialCompanyId > 0
          ? initialCompanyId
          : ""
      );
      setTouched(false);
      submitInFlightRef.current = false;
    }
  }, [open, initialCompanyId]);

  const nameError = !name.trim() ? "Group name is required" : "";
  const companyError = isSuperAdmin && companyId === "" ? "Please pick a company" : "";
  const valid = !nameError && !companyError;

  const handleSubmit = async () => {
    setTouched(true);
    if (!valid) return;
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      const payload: any = {
        name: name.trim(),
        description: description.trim() || null,
        playback_mode: playbackMode,
        is_active: isActive,
      };
      if (isSuperAdmin) payload.company_id = Number(companyId);
      const res: any = await createGroup(payload).unwrap();
      if (!res?.status) {
        showErrorMessage(res?.message || "Could not create playlist group");
        return;
      }
      showMessage("Playlist group created");
      onCreated(res.data as IPlaylistGroup);
      onClose();
    } catch (e: any) {
      showErrorMessage(extractBackendError(e, "Could not create playlist group"));
    } finally {
      submitInFlightRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.25, pr: 5, pb: 1 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
        }}>
          <CollectionsBookmarkIcon sx={{ color: "#fff", fontSize: 17 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={16} fontWeight={800} color="#0f172a">
            New Playlist Group
          </Typography>
          <Typography fontSize={11} color="#64748b" mt={0.25}>
            Create a group now — add playlists to it later from Playlist Groups.
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={isLoading}
          sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box>
          {isSuperAdmin && (
            <>
              <Typography sx={{ ...labelSx, display: "flex", alignItems: "center", gap: 0.5 }}>
                Company{" "}
                <Typography component="span" sx={{ color: "#ef4444", fontSize: 11 }}>*</Typography>
              </Typography>
              <TextField
                select fullWidth size="small"
                value={companyId === "" ? "" : String(companyId)}
                onChange={(e) =>
                  setCompanyId(e.target.value === "" ? "" : Number(e.target.value))
                }
                error={touched && !!companyError}
                helperText={touched && companyError}
                sx={{ ...inputSx, mb: 2 }}
              >
                <MenuItem value="">— Pick a company —</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.companyName}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          {!isSuperAdmin && currentUser?.company && (
            <>
              <Typography sx={labelSx}>Company</Typography>
              <Typography sx={{ fontSize: 13, color: "#475569", mb: 2 }}>
                {currentUser.company.name}
              </Typography>
            </>
          )}

          <Typography sx={{ ...labelSx, display: "flex", alignItems: "center", gap: 0.5 }}>
            Group Name{" "}
            <Typography component="span" sx={{ color: "#ef4444", fontSize: 11 }}>*</Typography>
          </Typography>
          <TextField
            autoFocus fullWidth size="small" placeholder="e.g. WingHQ Main Screen"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && !!nameError}
            helperText={touched && nameError}
            sx={{ ...inputSx, mb: 2 }}
          />

          <Typography sx={labelSx}>Playback Mode</Typography>
          <TextField
            select fullWidth size="small"
            value={playbackMode}
            onChange={(e) => setPlaybackMode(e.target.value)}
            sx={{ ...inputSx, mb: 2 }}
          >
            {PLAYBACK_MODES.map((m) => (
              <MenuItem key={m.v} value={m.v}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{m.help}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <Typography sx={labelSx}>Description</Typography>
          <TextField
            fullWidth size="small" multiline rows={2}
            placeholder="Optional — describe what this group plays and where…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ ...inputSx, mb: 2 }}
          />

          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            p: 1.5, borderRadius: 2,
            bgcolor: isActive ? alpha("#10b981", 0.05) : "#f8fafc",
            border: `1px solid ${isActive ? alpha("#10b981", 0.2) : "#e2e8f0"}`,
          }}>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Active</Typography>
              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                Inactive groups can't be assigned to devices
              </Typography>
            </Box>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button type="button" onClick={onClose} disabled={isLoading}
          sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
          Cancel
        </Button>
        <Button
          type="button" variant="contained" disableElevation
          disabled={!valid || isLoading}
          onClick={handleSubmit}
          startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 13, px: 3,
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
            "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
          }}
        >
          {isLoading ? "Creating..." : "Create Group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistGroupQuickCreateDialog;
